import React, { createContext, useState, useEffect, useContext } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`;
    const body = { email, password };
    // Diagnostic log (visible in production browser DevTools)
    console.log('[LOGIN] →', { url, method: 'POST', body: { email, password: '***' } });
    try {
      const response = await api.post('/auth/login', body);
      console.log('[LOGIN] ✓ status', response.status);

      // 2FA gate: backend may return {requires_2fa, partial_token, methods}
      if (response.data?.requires_2fa) {
        console.log('[LOGIN] 2FA required');
        return { requires_2fa: true, ...response.data };
      }

      const { token, user: userData } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userData));
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('[LOGIN] ✗', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message,
      });
      throw err;
    }
  };

  const completeTwoFactor = async (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Error refreshing user:', error);
      throw error;
    }
  };

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'kierownik';
  const permissions = user?.permissions || {};

  return (
    <AuthContext.Provider value={{ user, login, completeTwoFactor, logout, refreshUser, loading, isAdmin, isManager, permissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
